/**
 * Chat page caching utility
 * Implements stale-while-revalidate pattern for coaches list and layout metadata
 */

const ENABLED = true; // Feature flag for easy rollback
const CACHE_VERSION = 1;

// TTLs in milliseconds
const COACHES_TTL_MS = 10 * 60 * 1000; // 10 minutes
const LAYOUT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const MESSAGES_TTL_MS = 5 * 60 * 1000; // 5 minutes (shorter TTL for messages)

interface CoachData {
  id: string;
  name: string;
  emoji: string;
  avatarUrl?: string;
  tags?: string[];
  availability?: 'online' | 'away' | 'offline';
  lastActiveCoachId?: string;
  lastOpenedAt?: string;
}

interface LayoutData {
  tabs: string[];
  header?: string;
  featuredCoachIds?: string[];
}

interface ChatMessage {
  from: 'me' | 'them';
  text: string;
  ts: string;
  isTyping?: boolean;
  displayText?: string;
}

interface MessageHistory {
  messages: Record<string, ChatMessage[]>;
  pagination: Record<string, { hasMore: boolean; nextOffset: number; totalMessages: number }>;
}

interface CacheEntry<T> {
  data: T;
  updatedAt: number;
  ttlMs: number;
  version: number;
}

interface ChatCache {
  coaches?: CacheEntry<CoachData[]>;
  layout?: CacheEntry<LayoutData>;
  messages?: CacheEntry<MessageHistory>;
  updatedAt: number;
  version: number;
}

// Cache key generation
const getCacheKey = (userId: string) => `lifepsensor_chat_cache_${userId}`;

// Validation helpers
const isExpired = (entry: CacheEntry<any>, now: number = Date.now()): boolean => {
  return (now - entry.updatedAt) > entry.ttlMs;
};

const isValidVersion = (entry: CacheEntry<any>): boolean => {
  return entry.version === CACHE_VERSION;
};

// Core cache operations
export const getCachedCoaches = (userId: string): CoachData[] | null => {
  if (!ENABLED) return null;

  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;

    const cache: ChatCache = JSON.parse(raw);
    const coachesEntry = cache.coaches;

    if (!coachesEntry || !isValidVersion(coachesEntry)) return null;
    if (isExpired(coachesEntry)) return null; // Don't return expired data

    return coachesEntry.data;
  } catch (error) {

    return null;
  }
};

export const getCachedLayout = (userId: string): LayoutData | null => {
  if (!ENABLED) return null;

  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;

    const cache: ChatCache = JSON.parse(raw);
    const layoutEntry = cache.layout;

    if (!layoutEntry || !isValidVersion(layoutEntry)) return null;
    if (isExpired(layoutEntry)) return null; // Don't return expired data

    return layoutEntry.data;
  } catch (error) {

    return null;
  }
};

export const setCachedCoaches = (userId: string, coaches: CoachData[]): void => {
  if (!ENABLED) return;

  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    const cache: ChatCache = raw ? JSON.parse(raw) : {
      updatedAt: Date.now(),
      version: CACHE_VERSION
    };

    cache.coaches = {
      data: coaches,
      updatedAt: Date.now(),
      ttlMs: COACHES_TTL_MS,
      version: CACHE_VERSION
    };
    cache.updatedAt = Date.now();

    localStorage.setItem(cacheKey, JSON.stringify(cache));
  } catch (error) {

  }
};

export const setCachedLayout = (userId: string, layout: LayoutData): void => {
  if (!ENABLED) return;

  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    const cache: ChatCache = raw ? JSON.parse(raw) : {
      updatedAt: Date.now(),
      version: CACHE_VERSION
    };

    cache.layout = {
      data: layout,
      updatedAt: Date.now(),
      ttlMs: LAYOUT_TTL_MS,
      version: CACHE_VERSION
    };
    cache.updatedAt = Date.now();

    localStorage.setItem(cacheKey, JSON.stringify(cache));
  } catch (error) {

  }
};

// Update lightweight derived fields without full refetch
export const markTouched = (userId: string, updates: Partial<CoachData>): void => {
  if (!ENABLED) return;

  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return;

    const cache: ChatCache = JSON.parse(raw);
    if (cache.coaches) {
      // Update the coaches data with lightweight changes
      const updatedCoaches = cache.coaches.data.map(coach => ({
        ...coach,
        ...updates
      }));

      cache.coaches = {
        data: updatedCoaches,
        updatedAt: Date.now(),
        ttlMs: cache.coaches.ttlMs,
        version: CACHE_VERSION
      };
      cache.updatedAt = Date.now();

      localStorage.setItem(cacheKey, JSON.stringify(cache));
    }
  } catch (error) {

  }
};

// Clear all cache for a user
export const clearAll = (userId: string): void => {
  if (!ENABLED) return;

  try {
    const cacheKey = getCacheKey(userId);
    localStorage.removeItem(cacheKey);
  } catch (error) {

  }
};

// Clear legacy cache keys (for migration)
export const clearLegacyCache = (): void => {
  try {
    localStorage.removeItem('lifepsensor_chat_cache');
  } catch (error) {

  }
};

// Check if cache needs refresh (for SWR)
export const shouldRefreshCoaches = (userId: string): boolean => {
  if (!ENABLED) return true;

  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return true;

    const cache: ChatCache = JSON.parse(raw);
    const coachesEntry = cache.coaches;

    if (!coachesEntry || !isValidVersion(coachesEntry)) return true;
    return isExpired(coachesEntry);
  } catch (error) {
    return true;
  }
};

export const shouldRefreshLayout = (userId: string): boolean => {
  if (!ENABLED) return true;

  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return true;

    const cache: ChatCache = JSON.parse(raw);
    const layoutEntry = cache.layout;

    if (!layoutEntry || !isValidVersion(layoutEntry)) return true;
    return isExpired(layoutEntry);
  } catch (error) {
    return true;
  }
};

// Message history cache operations
export const getCachedMessages = (userId: string): MessageHistory | null => {
  if (!ENABLED) return null;

  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;

    const cache: ChatCache = JSON.parse(raw);
    const messagesEntry = cache.messages;

    if (!messagesEntry || !isValidVersion(messagesEntry)) return null;
    if (isExpired(messagesEntry)) return null; // Don't return expired messages

    return messagesEntry.data;
  } catch (error) {

    return null;
  }
};

export const setCachedMessages = (userId: string, messageHistory: MessageHistory): void => {
  if (!ENABLED) return;

  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    const cache: ChatCache = raw ? JSON.parse(raw) : {
      updatedAt: Date.now(),
      version: CACHE_VERSION
    };

    cache.messages = {
      data: messageHistory,
      updatedAt: Date.now(),
      ttlMs: MESSAGES_TTL_MS,
      version: CACHE_VERSION
    };
    cache.updatedAt = Date.now();

    localStorage.setItem(cacheKey, JSON.stringify(cache));
  } catch (error) {

  }
};

export const shouldRefreshMessages = (userId: string): boolean => {
  if (!ENABLED) return true;

  try {
    const cacheKey = getCacheKey(userId);
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return true;

    const cache: ChatCache = JSON.parse(raw);
    const messagesEntry = cache.messages;

    if (!messagesEntry || !isValidVersion(messagesEntry)) return true;
    return isExpired(messagesEntry);
  } catch (error) {
    return true;
  }
};

